/**
 * A table of figures that becomes a list of stacked rows when it does not fit.
 *
 * The OpenSilver report and money pages kept their fixed columns and let a phone pan the page
 * sideways. A canvas page that pans is harder to read than one that reflows, so this table decides
 * from its own width: wide enough for every column, it is a table with a header row and the figures
 * right-aligned in their columns; narrower, each row becomes its name over one line of
 * "Header value · Header value". It re-decides on every resize, so a rotation switches it.
 *
 * Rows are rebuilt on every `set` - a report is a few dozen rows that change only on a reload, so a
 * keyed diff would buy nothing.
 */
import { CrmLabel, type TextRole } from './Text.js';
import { FONT, GAP, fillWith, muted, relayout, splitRow, stackOf, text, vstack } from './layout.js';
import type { StackPanel } from 'webwin';

export interface TableColumn<T> {
  header: string;
  /** Fixed width of the column in table mode. */
  width: number;
  cell: (row: T) => string;
  role?: (row: T) => TextRole;
}

export interface TableOptions<T> {
  /** The first, growing column: the row's name. */
  header: string;
  name: (row: T) => string;
  /** A muted second line under the name (a SKU, a "retired" note). */
  detail?: (row: T) => string;
  columns: readonly TableColumn<T>[];
  /** Rows set in bold - a statement's subtotals. */
  strong?: (row: T) => boolean;
  /** Shown instead of rows when there are none; '' shows nothing at all. */
  empty?: string;
}

/** The narrowest the name column may be before the table gives up and stacks. */
const MIN_NAME_WIDTH = 160;

export class ReportTable<T> {
  readonly panel: StackPanel;
  readonly #options: TableOptions<T>;
  #rows: readonly T[] = [];
  #stacked: boolean | null = null;

  constructor(options: TableOptions<T>) {
    this.#options = options;
    this.panel = vstack({ spacing: GAP.xs });
    this.panel.on('resize', () => {
      if (this.#wantsStacked() !== this.#stacked) this.#render();
    });
  }

  set(rows: readonly T[]): void {
    this.#rows = rows;
    this.#render();
  }

  get needed(): number {
    const cols = this.#options.columns;
    return MIN_NAME_WIDTH + cols.reduce((sum, c) => sum + c.width, 0) + GAP.md * cols.length;
  }

  #wantsStacked(): boolean {
    // Before the first layout the panel has no width yet; a table is the better first guess on a
    // desktop and the resize that follows corrects it on a phone.
    return this.panel.width > 0 && this.panel.width < this.needed;
  }

  #render(): void {
    const { columns, empty } = this.#options;
    this.#stacked = this.#wantsStacked();
    this.panel.controls.clear();

    if (this.#rows.length === 0) {
      if (empty) this.panel.controls.add(muted(empty));
      relayout(this.panel);
      return;
    }

    if (!this.#stacked) {
      const head = splitRow({ spacing: GAP.md, align: 'end' });
      fillWith(head, cellLabel(this.#options.header, MIN_NAME_WIDTH, 'left', 'muted', true));
      for (const c of columns) head.controls.add(cellLabel(c.header, c.width, 'right', 'muted', true));
      this.panel.controls.add(head);
    }

    for (const row of this.#rows) this.panel.controls.add(this.#stacked ? this.#stackedRow(row) : this.#tableRow(row));
    relayout(this.panel);
  }

  #nameBlock(row: T): StackPanel {
    const bold = this.#options.strong?.(row) ?? false;
    const block = stackOf(vstack({ spacing: 0 }), text(this.#options.name(row), bold ? { font: FONT.bold } : {}));
    const detail = this.#options.detail?.(row);
    if (detail) block.controls.add(muted(detail));
    return block;
  }

  #tableRow(row: T): StackPanel {
    const line = splitRow({ spacing: GAP.md, align: 'start' });
    fillWith(line, this.#nameBlock(row));
    for (const c of this.#options.columns) {
      const label = cellLabel(c.cell(row), c.width, 'right', c.role?.(row) ?? 'text', false);
      if (this.#options.strong?.(row)) label.font = FONT.bold;
      line.controls.add(label);
    }
    return line;
  }

  #stackedRow(row: T): StackPanel {
    const block = this.#nameBlock(row);
    block.margin = { left: 0, top: GAP.xs, right: 0, bottom: GAP.xs };
    const parts = this.#options.columns.map(c => `${c.header} ${c.cell(row)}`);
    block.controls.add(text(parts.join(' · '), { font: FONT.small }));
    return block;
  }
}

function cellLabel(content: string, width: number, align: 'left' | 'right', role: TextRole, header: boolean): CrmLabel {
  return new CrmLabel({
    text: content,
    width,
    autoWidth: false,
    autoHeight: true,
    wrap: false,
    ellipsis: true,
    textAlign: align,
    // Top, like the name beside it: centred in a row the name made taller, a figure sits low.
    verticalAlign: 'top',
    font: header ? FONT.small : FONT.body,
    role
  });
}
